import { useParams } from 'react-router-dom';

import { useGetAssetByIdQuery } from '@/store/api/assetsApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

import { getMetaValue } from '@/utils/assetMeta';
import { BackButton } from '@/components/common/BackButton';
import { ExternalLink } from 'lucide-react';

export default function AssetDetailsPage() {
  // const { id } = useParams<{ id: string }>();
  // const assetId = Number(id);
  const { assetId } = useParams<{ assetId: string }>(); // new 5 update
  const id = Number(assetId); // new 5 update

  const {
    data: asset,
    isLoading,
    isError,
    // } = useGetAssetByIdQuery(assetId, {
    //   skip: !assetId,
    // });
    // from new 5 update
  } = useGetAssetByIdQuery(id, {
    skip: !id,
  });
  // from new 5 update

  if (isLoading) return <div className="p-6">Loading asset details…</div>;
  if (isError || !asset) return <div className="p-6 text-red-500">Failed to load asset</div>;

  const meta = asset.meta ?? [];
  const documents = asset.documents ?? [];
  const propertyInterest = asset.propertyInterest ?? [];

  return (
    <div className="space-y-8 p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-10">
        <div className="flex items-center gap-10">
          <BackButton />
          <div>
            <h1 className="text-3xl font-bold">{asset.assetTitle ?? '—'}</h1>
            <p className="text-muted-foreground">Owned by {asset.currentOwnerName ?? '—'}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Badge variant={asset.isVerified ? 'default' : 'secondary'}>
            {asset.isVerified ? 'Verified' : 'Pending Verification'}
          </Badge>
          {asset.isSoldOut && <Badge variant="destructive">Sold Out</Badge>}
        </div>
      </div>

      {/* BASIC INFO */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <Info label="Asset Type" value={asset.assetType} />
          <Info label="Asset Sub Type" value={getMetaValue(meta, 'assetSubType')} />
          <Info label="Ownership Model" value={asset.ownershipModel} />
          <Info label="Created At" value={formatDate(asset.createdAt)} />
        </CardContent>
      </Card>

      {/* LOCATION */}
      <Card>
        <CardHeader>
          <CardTitle>Location</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="City" value={getMetaValue(meta, 'city')} />
          <Info label="State" value={getMetaValue(meta, 'state')} />
          <Info label="Country" value={getMetaValue(meta, 'country')} />
          <Info label="Pincode" value={getMetaValue(meta, 'pincode')} />
          <Info label="Geo Location" value={getMetaValue(meta, 'geoLocation')} />
        </CardContent>
      </Card>

      {/* PROPERTY DETAILS */}
      <Card>
        <CardHeader>
          <CardTitle>Property Details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="Built-up Area" value={getMetaValue(meta, 'builtupArea')} />
          <Info label="Land Area" value={getMetaValue(meta, 'landArea')} />
          <Info label="Year of Construction" value={getMetaValue(meta, 'yearOfConstruction')} />
          <Info label="Asset Age" value={getMetaValue(meta, 'assetAge')} />
          <Info label="Title Status" value={getMetaValue(meta, 'titleStatus')} />
          <Info label="RERA Number" value={getMetaValue(meta, 'reraRegistrationNumber')} />
        </CardContent>
      </Card>

      {/* FINANCIALS */}
      <Card>
        <CardHeader>
          <CardTitle>Financials</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <Info label="Market Value" value={getMetaValue(meta, 'marketValue')} />
          <Info label="Rental Income (Monthly)" value={getMetaValue(meta, 'rentalIncomeMonthly')} />
          <Info label="Lease Rent / Month" value={getMetaValue(meta, 'leaseRentPerMonth')} />
          <Info
            label="Lease Tenure (Months)"
            value={getMetaValue(meta, 'leaseTenureRemainingMonths')}
          />
          <Info label="Last 3 Year Revenue" value={getMetaValue(meta, 'last3YearRevenue')} />
        </CardContent>
      </Card>

      {/* DESCRIPTION */}
      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 break-all">
          <p>{getMetaValue(meta, 'shortDescription') ?? '—'}</p>
          <Separator />
          <p className="text-muted-foreground break-all">
            {getMetaValue(meta, 'detailedDescription') ?? '—'}
          </p>
        </CardContent>
      </Card>

      {/* DOCUMENTS */}
      <Card>
        <CardContent className="space-y-3">
          {documents.length === 0 && <p className="text-muted-foreground">No documents uploaded</p>}
          <div>
            <CardHeader>
              <CardTitle>Asset Images</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter((item) => item?.documentType === 'ASSET_IMAGE')
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    <img src={doc.url} alt="Document" className="h-48 w-full object-cover" />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>
                  </a>
                ))}
            </div>
          </div>
          <div>
            <CardHeader>
              <CardTitle>Asset Videos</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter((item) => item?.documentType === 'ASSET_VIDEO')
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    <video
                      src={doc.url}
                      className="h-48 w-full object-cover"
                      muted
                      playsInline
                      controls
                      preload="metadata"
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }}
                    />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>
                  </a>
                ))}
            </div>
          </div>
          <div>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {documents
                .filter(
                  (item) =>
                    item?.documentType !== 'ASSET_IMAGE' && item?.documentType !== 'ASSET_VIDEO'
                )
                .map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative border rounded-md overflow-hidden"
                  >
                    {/* <img src={doc.url} alt="Document" className="h-48 w-full object-cover" /> */}
                    {doc.url?.toLowerCase().endsWith('.pdf') ? (
                      <div className="h-48 w-full flex flex-col items-center justify-center bg-[var(--inner-card)] gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 text-[var(--fg)]/50"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM9.3 15.1c-.2.2-.5.3-1 .3H8v1.1H7v-3.5h1.3c.5 0 .8.1 1 .3.2.2.3.5.3.9s-.1.7-.3.9zm3.7.2c-.2.3-.5.5-.8.6-.2.1-.5.1-.9.1H10v-3.5h1.3c.4 0 .7.1.9.1.3.1.6.3.8.6.2.3.3.6.3 1s-.1.8-.3 1.1zm3.5-2.3h-1.8v.8h1.6v.7h-1.6v1.4H13v-3.5h3.5v.6z" />
                        </svg>
                        <span className="text-[var(--fg)]/60 text-sm font-medium">
                          PDF Document
                        </span>
                      </div>
                    ) : (
                      <img src={doc.url} alt="Document" className="h-48 w-full object-cover" />
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <ExternalLink className="text-white h-6 w-6" />
                    </div>

                    <div className="p-2 text-xs flex justify-between items-center bg-muted">
                      <span className="capitalize"> #{doc.documentType}</span>
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WHY INVESTED / PROPERTY INTEREST */}
      <Card>
        <CardHeader>
          <CardTitle>Why Invest in this Property</CardTitle>
        </CardHeader>

        <CardContent>
          {propertyInterest.length === 0 ? (
            <p className="text-muted-foreground">No data available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {propertyInterest.map((item: any, index: number) => (
                <div key={index} className="flex gap-4 p-4 border rounded-xl bg-muted/30">
                  {/* ICON */}
                  <img
                    src={item.icon}
                    alt={item.title}
                    className="w-10 h-10 object-contain rounded-md bg-black p-1"
                  />

                  {/* TEXT */}
                  <div>
                    <h4 className="font-semibold text-base">{item.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function Info({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium">{value ?? '—'}</p>
    </div>
  );
}

function formatDate(date?: string) {
  return date ? new Date(date).toLocaleDateString() : '—';
}
